import {
  startListenerCompetitorAdFavorites,
  startRemoveCompetitorAdFavorite,
  startSetCompetitorAdFavorite,
  stopListenerCompetitorAdFavorites,
} from "../../../../actions/competitorAdsFavorites";
import {
  buildCompetitorAdFavoritePayload,
  isCompetitorAdFavorite,
} from "../../../../services/competitorAdsGallery";
import { startSaveCompetitorFavoriteMetered } from "../../../../actions/featureAddons";
import useCompetitorsAddon from "../../../../hooks/useCompetitorsAddon";
import { useCallback, useEffect, useRef, useState } from "react";

const applyOptimisticFavoriteAdd = ({
  favoritesMap,
  pageId,
  libraryID,
  item,
}) => {
  const payload = buildCompetitorAdFavoritePayload({
    pageId,
    libraryID,
    ad: item,
  });
  const next = { ...(favoritesMap || {}) };
  next[pageId] = {
    ...(next[pageId] || {}),
    [libraryID]: payload,
  };
  return next;
};

const applyOptimisticFavoriteRemove = ({ favoritesMap, pageId, libraryID }) => {
  if (!favoritesMap?.[pageId]?.[libraryID]) return favoritesMap;

  const next = { ...favoritesMap };
  const pageFavorites = { ...next[pageId] };
  delete pageFavorites[libraryID];

  if (Object.keys(pageFavorites).length === 0) {
    delete next[pageId];
  } else {
    next[pageId] = pageFavorites;
  }

  return next;
};

export const useCompetitorsFavorites = ({ modalOpen, dispatch }) => {
  const [favoritesByPageId, setFavoritesByPageId] = useState(null);
  const listenerRef = useRef(null);
  const paywall = useCompetitorsAddon();
  const useMeteredFavorites = paywall.eligible;
  const inFlightFavoriteKeysRef = useRef(new Set());

  useEffect(() => {
    if (!modalOpen) {
      setFavoritesByPageId(null);
      return undefined;
    }

    dispatch(
      startListenerCompetitorAdFavorites({
        onUpdatedDataCallback: setFavoritesByPageId,
        onReferenceAvailable: (dbRef) => {
          listenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCompetitorAdFavorites({ listenerRef: listenerRef.current })
      );
      listenerRef.current = null;
    };
  }, [dispatch, modalOpen]);

  const rollbackFavoriteToggle = useCallback(
    ({ pageId, libraryID, wasFavorite, item }) => {
      setFavoritesByPageId((current) =>
        wasFavorite
          ? applyOptimisticFavoriteAdd({
              favoritesMap: current,
              pageId,
              libraryID,
              item,
            })
          : applyOptimisticFavoriteRemove({
              favoritesMap: current,
              pageId,
              libraryID,
            })
      );
    },
    []
  );

  const persistFavoriteChange = useCallback(
    ({ pageId, libraryID, item, wasFavorite }) => {
      if (wasFavorite) {
        return dispatch(
          startRemoveCompetitorAdFavorite({ pageId, libraryID })
        ).then((response) => {
          if (!response?.ok) {
            rollbackFavoriteToggle({ pageId, libraryID, wasFavorite, item });
          }
          return response;
        });
      }

      if (useMeteredFavorites) {
        const favorite = buildCompetitorAdFavoritePayload({
          pageId,
          libraryID,
          ad: item,
        });
        return dispatch(
          startSaveCompetitorFavoriteMetered({
            pageId,
            libraryID,
            favorite,
          })
        ).then((response) => {
          if (!response?.ok) {
            rollbackFavoriteToggle({ pageId, libraryID, wasFavorite, item });
            paywall.openPaywallFromLimitError(response);
          }
          return response;
        });
      }

      return dispatch(
        startSetCompetitorAdFavorite({ pageId, libraryID, ad: item })
      ).then((response) => {
        if (!response?.ok) {
          rollbackFavoriteToggle({ pageId, libraryID, wasFavorite, item });
        }
        return response;
      });
    },
    [dispatch, paywall, rollbackFavoriteToggle, useMeteredFavorites]
  );

  const isFavorite = useCallback(
    ({ pageId, libraryID, item = null }) =>
      isCompetitorAdFavorite({
        favoritesMap: favoritesByPageId,
        pageId,
        libraryID,
        item,
      }),
    [favoritesByPageId]
  );

  const reserveFavoriteSlot = useCallback(
    async (item) => {
      if (!useMeteredFavorites) return { allowed: true, held: false };
      const blocked = await paywall.blockIfUsageReached({
        censored: item?.featureAddonCensored,
      });
      return { allowed: !blocked, held: !blocked };
    },
    [paywall, useMeteredFavorites]
  );

  const saveToFavorites = useCallback(
    async ({ item }) => {
      const pageId = item?.pageId;
      const libraryID = item?.libraryID ?? item?.id;

      if (!pageId || !libraryID || !item?.url) {
        return { ok: false };
      }

      const favoriteKey = `${pageId}::${libraryID}`;
      if (inFlightFavoriteKeysRef.current.has(favoriteKey)) {
        return { ok: false };
      }

      const wasFavorite = isCompetitorAdFavorite({
        favoritesMap: favoritesByPageId,
        pageId,
        libraryID,
        item,
      });

      if (wasFavorite) {
        return { ok: true };
      }

      inFlightFavoriteKeysRef.current.add(favoriteKey);
      let held = false;
      try {
        const slot = await reserveFavoriteSlot(item);
        if (!slot.allowed) return { ok: false };
        held = slot.held;

        setFavoritesByPageId((current) =>
          applyOptimisticFavoriteAdd({
            favoritesMap: current,
            pageId,
            libraryID,
            item,
          })
        );

        return await persistFavoriteChange({
          pageId,
          libraryID,
          item,
          wasFavorite: false,
        });
      } finally {
        inFlightFavoriteKeysRef.current.delete(favoriteKey);
        if (held) paywall.releasePendingAction();
      }
    },
    [favoritesByPageId, paywall, persistFavoriteChange, reserveFavoriteSlot]
  );

  const removeFromFavorites = useCallback(
    async ({ item }) => {
      const pageId = item?.pageId;
      const libraryID = item?.libraryID ?? item?.id;

      if (!pageId || !libraryID) {
        return { ok: false };
      }

      const wasFavorite = isCompetitorAdFavorite({
        favoritesMap: favoritesByPageId,
        pageId,
        libraryID,
        item,
      });

      if (!wasFavorite) {
        return { ok: true };
      }

      setFavoritesByPageId((current) =>
        applyOptimisticFavoriteRemove({
          favoritesMap: current,
          pageId,
          libraryID,
        })
      );

      return persistFavoriteChange({
        pageId,
        libraryID,
        item,
        wasFavorite: true,
      });
    },
    [favoritesByPageId, persistFavoriteChange]
  );

  const onAddFavorites = useCallback(
    async ({ items = [] }) => {
      const itemsToAdd = items.filter((item) => {
        const pageId = item?.pageId;
        const libraryID = item?.libraryID ?? item?.id;
        if (!pageId || !libraryID || !item?.url) return false;

        return !isCompetitorAdFavorite({
          favoritesMap: favoritesByPageId,
          pageId,
          libraryID,
          item,
        });
      });

      if (!itemsToAdd.length) return;

      const accepted = [];
      for (const item of itemsToAdd) {
        const pageId = item.pageId;
        const libraryID = item.libraryID ?? item.id;
        const favoriteKey = `${pageId}::${libraryID}`;
        if (inFlightFavoriteKeysRef.current.has(favoriteKey)) continue;

        inFlightFavoriteKeysRef.current.add(favoriteKey);
        const slot = await reserveFavoriteSlot(item);
        if (!slot.allowed) {
          inFlightFavoriteKeysRef.current.delete(favoriteKey);
          break;
        }
        accepted.push({
          item,
          pageId,
          libraryID,
          favoriteKey,
          held: slot.held,
        });
      }

      if (!accepted.length) return;

      setFavoritesByPageId((current) => {
        let next = current;
        accepted.forEach(({ item, pageId, libraryID }) => {
          next = applyOptimisticFavoriteAdd({
            favoritesMap: next,
            pageId,
            libraryID,
            item,
          });
        });
        return next;
      });

      accepted.forEach(({ item, pageId, libraryID, favoriteKey, held }) => {
        persistFavoriteChange({
          pageId,
          libraryID,
          item,
          wasFavorite: false,
        }).finally(() => {
          inFlightFavoriteKeysRef.current.delete(favoriteKey);
          if (held) paywall.releasePendingAction();
        });
      });
    },
    [favoritesByPageId, paywall, persistFavoriteChange, reserveFavoriteSlot]
  );

  const getFavoriteState = useCallback(
    (item) => ({
      isFavorite: isFavorite({
        pageId: item?.pageId,
        libraryID: item?.libraryID ?? item?.id,
        item,
      }),
      isLoading: false,
    }),
    [isFavorite]
  );

  return {
    favoritesByPageId,
    isFavorite,
    saveToFavorites,
    removeFromFavorites,
    onAddFavorites,
    getFavoriteState,
    paywall,
  };
};

export default useCompetitorsFavorites;
